import { getTheme, ITextFieldProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPIconButton, IPLabel, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import TaskModifyCompletionDate from 'components/Tasks/TaskModifyCompletionDate';
import {
  editIcon,
  getGlobalTextFieldDisabled,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Task, { TaskStateId } from 'models/tasks/task';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { useIsInTaskDetailCallout } from './TaskDetailRow';
import { dropdownStyles, iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailStatusProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  showCompleted: boolean;
  onUpdate: (newTask: Task) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  controlsOnly?: boolean;
}

export const TaskDetailStatus = (props: ITaskDetailStatusProps) => {
  const isInCallout = useIsInTaskDetailCallout();
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const [showEditCompletionDate, setShowEditCompletionDate] = useState<boolean>(false);
  const theme = getTheme();
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Status, appContext);

  const getStatusOptions = (): IPSelectableOption[] => {
    if (
      props.orgTask.taskStateId === TaskStateId.Completed ||
      props.orgTask.taskStateId === TaskStateId.Rejected ||
      props.orgTask.taskStateId === TaskStateId.Archived
    ) {
      return appContext.globalDataCache.taskStates.items.map((_taskState) => {
        return {
          key: _taskState.taskStateId.toString(),
          text: _taskState.state,
        };
      });
    } else {
      return appContext.globalDataCache.taskStates.items
        .filter((taskState) => taskState.taskStateId !== TaskStateId.Archived)
        .map((_taskState) => {
          return {
            key: _taskState.taskStateId.toString(),
            text: _taskState.state,
          };
        });
    }
  };

  const getCompletedLabel = (
    textProps?: ITextFieldProps,
    defaultRender?: (textProps?: ITextFieldProps) => JSX.Element | null,
  ): JSX.Element => {
    if (hasUserFeature(appContext, FeatureTypes.TaskChangeCompletionDate) && props.task.completed) {
      return (
        <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
          <IPLabel>{t('task:Completed.Label')}</IPLabel>
          <IPIconButton
            onClick={() => {
              setShowEditCompletionDate(true);
            }}
            iconProps={editIcon}
            styles={{ root: { height: 18 } }}
          />
          <TaskModifyCompletionDate
            task={props.orgTask}
            isOpen={showEditCompletionDate}
            onSave={(newCompletionDate) => {
              if (newCompletionDate) props.onChangeCompletionDate(newCompletionDate);
            }}
            onClose={() => setShowEditCompletionDate(false)}
          />
        </IPStack>
      );
    } else {
      return <IPLabel>{t('task:Completed.Label')}</IPLabel>;
    }
  };

  const getContent = () => {
    if (isReadonly) {
      return (
        <Fragment>
          <IPStack.Item styles={{ root: { width: '50%' } }}>
            <IPLabel>{t('task:Status.Label')}</IPLabel>
            <IPText>{appContext.globalDataCache.taskStates.get(props.task.taskStateId).state}</IPText>
          </IPStack.Item>
          {props.showCompleted && (
            <IPStack.Item styles={{ root: { width: '50%' } }}>
              <IPLabel>{t('task:Completed.Label')}</IPLabel>
              <IPText>{toLocaleDateTimeMedium(props.task.completed) || '-'}</IPText>
            </IPStack.Item>
          )}
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <IPStack.Item grow>
            <IPDropdown
              selectedKey={props.task.taskStateId.toString()}
              options={getStatusOptions()}
              label={t('task:Status.Label')}
              styles={dropdownStyles}
              onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                if (!option) return;
                const copyTask = props.task.clone();
                copyTask.taskStateId = +option.key;
                props.onUpdate(copyTask);
              }}
            />
          </IPStack.Item>
          {props.showCompleted && (
            <IPStack.Item>
              <IPTextField
                onRenderLabel={getCompletedLabel}
                styles={getGlobalTextFieldDisabled(appContext, theme, 220)}
                readOnly
                value={toLocaleDateTimeMedium(props.task.completed)}
              ></IPTextField>
            </IPStack.Item>
          )}
        </Fragment>
      );
    }
  };

  //
  // Main render
  //
  if (props.controlsOnly) {
    return getContent();
  } else {
    return (
      <IPStack
        horizontal
        tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
        verticalAlign="start"
        styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace } } : undefined}
      >
        {!isInCallout && (
          <IPStack.Item>
            {!appContext.isMobileView && <IPFontIcon iconName="FavoriteStarFill" style={iconFontSize} />}
          </IPStack.Item>
        )}
        {getContent()}
      </IPStack>
    );
  }
};
